/**
 * Aged demo fixtures for the /mycraftz sandbox.
 *
 * Two of the five showcase CraftPrints get scripted "what accumulated practice
 * looks like" previews so a visitor can see a partial week (day 7) and a
 * finished cycle (day 30) WITHOUT waiting real time. These are ILLUSTRATIVE
 * fixtures — fixed, hand-written, browser-local. They are never live-computed
 * and never represent a real account's history.
 *
 * Keyed by showcase slug. Only slugs present here get the aged-preview control.
 */

// Build a kept/missed day strip. `missed` is a set of 1-indexed day numbers.
function strip(total, missedDays) {
  const missed = new Set(missedDays);
  return Array.from({ length: total }, (_, i) => !missed.has(i + 1));
}

export const AGED_DEMO_FIXTURES = {
  "atomic-habits": {
    ideaName: "Identity-Based Habits",
    practiceLine:
      "After my morning coffee, write one line as the person I'm becoming.",
    week: {
      // 5 of 7 kept — two gentle misses, one with a logged coping reflection.
      keptLabel: "5 of 7 days kept",
      kept: 5,
      total: 7,
      days: strip(7, [3, 6]),
      coping: {
        day: 3,
        vighna: "Day 3 ran away from me — back-to-back meetings, no coffee ritual.",
        upaya:
          "Forgave the miss and did a 2-minute version that night instead. The chain didn't 'break' — it just paused.",
      },
      siddhiGate:
        "Two of your three practices are still mid-stream. Siddhi — your retrospective — opens once a full cycle completes.",
    },
    month: {
      // 26 of 30 kept — a completed cycle with the finished retrospective.
      keptLabel: "26 of 30 days kept",
      kept: 26,
      total: 30,
      days: strip(30, [3, 6, 17, 24]),
      retro: {
        phalaGuess:
          "At commitment you guessed: “I'll feel a bit more disciplined about my mornings.”",
        phalaActual:
          "What actually arrived: you stopped needing motivation. Writing as “a healthy, deliberate person” became automatic — the identity started pulling the habit, not the other way round.",
        vighnaGuess:
          "You expected the obstacle to be: “I'll probably forget on busy work days.”",
        vighnaActual:
          "What really got in the way: busy days were fine. Travel days were the gap — all 4 misses happened while you were away from home.",
        evidence:
          "26 of 30 days kept · longest unbroken run 11 days · 4 misses, every one recovered the next morning",
        identityExample:
          "I am someone who keeps small promises to myself — every day, and gently again the day after a slip.",
      },
    },
  },

  "deep-work": {
    ideaName: "Deep Work Blocks",
    practiceLine:
      "Before opening email, protect one 90-minute block for my most important work.",
    week: {
      keptLabel: "5 of 7 days kept",
      kept: 5,
      total: 7,
      days: strip(7, [4, 7]),
      coping: {
        day: 4,
        vighna: "Day 4 the block got eaten by a 'quick' call that wasn't quick.",
        upaya:
          "Named it, didn't spiral. Blocked the calendar the night before so Day 5 was protected by default.",
      },
      siddhiGate:
        "Your focus practice is still mid-stream. Siddhi — the retrospective that compares your intention to what actually happened — opens once the cycle completes.",
    },
    month: {
      keptLabel: "26 of 30 days kept",
      kept: 26,
      total: 30,
      days: strip(30, [4, 7, 15, 22]),
      retro: {
        phalaGuess:
          "At commitment you guessed: “I'll get a little more done in the mornings.”",
        phalaActual:
          "What actually arrived: the shallow, reactive work shrank on its own. One protected block a day quietly moved the projects that had been stuck for months.",
        vighnaGuess:
          "You expected the obstacle to be: “I won't have the willpower to focus that long.”",
        vighnaActual:
          "What really got in the way: willpower was never the problem — unguarded calendars were. The 4 misses were all days you hadn't blocked the time in advance.",
        evidence:
          "26 of 30 blocks kept · longest unbroken run 9 days · 4 misses, each on an un-blocked calendar",
        identityExample:
          "I am someone who defends one block of real focus a day — and defends it the night before, not in the moment.",
      },
    },
  },

  "psychology-of-money": {
    ideaName: "48-Hour Spending Rule",
    practiceLine:
      "Before any non-essential buy over ₹2,000, add it to a wait-list and let 48 hours pass before I decide.",
    week: {
      keptLabel: "5 of 7 days kept",
      kept: 5,
      total: 7,
      days: strip(7, [2, 5]),
      coping: {
        day: 2,
        vighna: "A midnight flash-sale caught me mid-scroll — I bought the gadget before the 48 hours were up.",
        upaya:
          "Logged the slip honestly instead of pretending it didn't happen, then moved the shopping apps off my home screen so the next impulse had one more step to climb.",
      },
      siddhiGate:
        "The rule is still finding its rhythm. Siddhi — where your first guess meets what actually happened to your spending — opens once the cycle completes.",
    },
    month: {
      keptLabel: "26 of 30 days kept",
      kept: 26,
      total: 30,
      days: strip(30, [2, 5, 16, 27]),
      retro: {
        phalaGuess:
          "At commitment you guessed: “I'll probably save a bit of money.”",
        phalaActual:
          "What actually arrived: the savings were the small part. Most “wants” quietly evaporated inside the 48 hours — you weren't white-knuckling past purchases, you simply stopped wanting most of them.",
        vighnaGuess:
          "You expected the obstacle to be: “I'll cave whenever something's on sale.”",
        vighnaActual:
          "What really got in the way: sales were never the trap — late-night phone scrolling was. All 4 slips happened after 10pm, none of them in daylight.",
        evidence:
          "26 of 30 days honoured · 14 items sat out the wait, 11 were never bought · 4 slips, every one late at night",
        identityExample:
          "I am someone who lets a want prove itself for 48 hours before I let it cost me anything.",
      },
    },
  },

  "think-and-grow-rich": {
    ideaName: "Purpose Statement",
    practiceLine:
      "Read my one-line definite purpose aloud — once when I wake, once before I sleep.",
    week: {
      keptLabel: "5 of 7 days kept",
      kept: 5,
      total: 7,
      days: strip(7, [3, 7]),
      coping: {
        day: 3,
        vighna: "Fell asleep before the night reading two evenings this week — willpower ran out before bedtime did.",
        upaya:
          "Didn't treat it as failure. Taped the statement to the bathroom mirror so it became the last thing I see, not one more thing I have to remember.",
      },
      siddhiGate:
        "Your purpose is still settling in. Siddhi — the retrospective that shows how a rehearsed purpose actually changed your choices — opens once the cycle completes.",
    },
    month: {
      keptLabel: "26 of 30 days kept",
      kept: 26,
      total: 30,
      days: strip(30, [3, 7, 19, 26]),
      retro: {
        phalaGuess:
          "At commitment you guessed: “I'll feel more motivated in the mornings.”",
        phalaActual:
          "What actually arrived: motivation was the wrong word. By week three the statement was making decisions for you — you were turning down things that didn't serve the purpose, almost without noticing.",
        vighnaGuess:
          "You expected the obstacle to be: “I'll feel silly reading it out loud.”",
        vighnaActual:
          "What really got in the way: the awkwardness was gone in days. Tired evenings were the real gap — you kept every morning, but nights cost you all 4 misses.",
        evidence:
          "26 of 30 days rehearsed · 30/30 mornings, 26/30 nights · statement itself revised twice as it sharpened",
        identityExample:
          "I am someone with a purpose specific enough to say no on its behalf.",
      },
    },
  },

  "mans-search-for-meaning": {
    ideaName: "Meaning Journal",
    practiceLine:
      "Each evening, write one moment that mattered today — however small — or one thing I was responsible to.",
    week: {
      keptLabel: "5 of 7 days kept",
      kept: 5,
      total: 7,
      days: strip(7, [4, 6]),
      coping: {
        day: 4,
        vighna: "Day 4 was heavy — I didn't want to look at it, so I closed the journal and skipped.",
        upaya:
          "Wrote two honest lines the next morning instead of forcing it that night. The point was never a perfect streak — it was telling the truth about the day.",
      },
      siddhiGate:
        "You're still mid-stream. Siddhi — where you see what naming meaning actually did to your hardest days — opens once the cycle completes.",
    },
    month: {
      keptLabel: "26 of 30 days kept",
      kept: 26,
      total: 30,
      days: strip(30, [4, 6, 18, 25]),
      retro: {
        phalaGuess:
          "At commitment you guessed: “I'll end up with a nice record of my good days.”",
        phalaActual:
          "What actually arrived: it wasn't the good days that changed you. Naming meaning inside the hard ones — a call you made, a responsibility you kept — is what shifted how you carried them.",
        vighnaGuess:
          "You expected the obstacle to be: “I'll run out of things worth writing.”",
        vighnaActual:
          "What really got in the way: you never ran dry. The misses landed on the heaviest days — the exact days this practice was built for. Seeing that was its own turning point.",
        evidence:
          "26 of 30 evenings written · longest unbroken run 12 days · 4 misses, all on the hardest days",
        identityExample:
          "I am someone who can find a 'why' inside almost any day — especially the heavy ones.",
      },
    },
  },
};

export function getAgedFixture(slug) {
  return AGED_DEMO_FIXTURES[slug] || null;
}
